// =============================================================================
// BP-07-03: RatingWidget Component
// =============================================================================

import { useState } from 'react';
import { Star, Loader2 } from 'lucide-react';
import { useRateResource } from '@/features/resources/hooks/useResource';
import { cn } from '@/core/lib/utils';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

interface RatingWidgetProps {
  resourceId: string;
  avgRating?: number | null;
  ratingCount?: number;
  userRating?: number | null;
  size?: 'sm' | 'md' | 'lg';
  interactive?: boolean;
  showCount?: boolean;
  className?: string;
  onRate?: (rating: number) => void;
}

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export function RatingWidget({
  resourceId,
  avgRating,
  ratingCount = 0,
  userRating,
  size = 'md',
  interactive = true,
  showCount = true,
  className,
  onRate,
}: RatingWidgetProps) {
  const [hoveredStar, setHoveredStar] = useState<number | null>(null);
  const [selectedRating, setSelectedRating] = useState<number | null>(userRating ?? null);
  const { mutate: rateResource, isPending } = useRateResource();

  const displayRating = hoveredStar ?? selectedRating ?? avgRating ?? 0;

  const handleRate = (rating: number) => {
    if (!interactive || isPending) return;

    setSelectedRating(rating);
    rateResource(
      { resourceId, rating },
      {
        onSuccess: () => {
          onRate?.(rating);
        },
        onError: () => {
          setSelectedRating(userRating ?? null);
        },
      }
    );
  };

  // Size configurations
  const sizeConfig = {
    sm: { star: 'w-4 h-4', gap: 'gap-0.5', text: 'text-xs' },
    md: { star: 'w-5 h-5', gap: 'gap-1', text: 'text-sm' },
    lg: { star: 'w-6 h-6', gap: 'gap-1.5', text: 'text-base' },
  };

  const config = sizeConfig[size];

  // Render stars
  const renderStars = () => {
    return [1, 2, 3, 4, 5].map((star) => {
      const isFilled = star <= displayRating;
      const isHalfFilled = !isFilled && star - 0.5 <= displayRating;

      return (
        <button
          key={star}
          type="button"
          onClick={() => handleRate(star)}
          onMouseEnter={() => interactive && setHoveredStar(star)}
          onMouseLeave={() => interactive && setHoveredStar(null)}
          disabled={!interactive || isPending}
          className={cn(
            'transition-transform duration-100',
            interactive && 'hover:scale-110 cursor-pointer',
            !interactive && 'cursor-default',
            isPending && 'opacity-50'
          )}
          aria-label={`Rate ${star} stars`}
        >
          {isHalfFilled ? (
            <HalfStar className={cn(config.star, 'text-terracotta')} />
          ) : (
            <Star
              className={cn(
                config.star,
                isFilled
                  ? 'fill-terracotta text-terracotta'
                  : 'text-warm-gray/30'
              )}
            />
          )}
        </button>
      );
    });
  };

  return (
    <div className={cn('flex items-center', config.gap, className)}>
      {/* Stars */}
      <div className={cn('flex items-center', config.gap)}>
        {isPending ? (
          <Loader2 className={cn(config.star, 'animate-spin text-warm-gray')} />
        ) : (
          renderStars()
        )}
      </div>

      {/* Rating info */}
      {(avgRating !== null || showCount) && (
        <div className={cn('flex items-center', config.gap, config.text, 'text-warm-gray ml-1')}>
          {avgRating !== null && avgRating > 0 && (
            <span className="font-medium text-navy">{avgRating.toFixed(1)}</span>
          )}
          {showCount && ratingCount > 0 && (
            <span>({ratingCount.toLocaleString()})</span>
          )}
        </div>
      )}

      {/* User rating indicator */}
      {selectedRating && interactive && (
        <span className={cn(config.text, 'text-sage ml-2')}>
          Your rating: {selectedRating}
        </span>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Half Star SVG
// -----------------------------------------------------------------------------

function HalfStar({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <defs>
        <linearGradient id="halfFill">
          <stop offset="50%" stopColor="currentColor" />
          <stop offset="50%" stopColor="transparent" />
        </linearGradient>
      </defs>
      <polygon
        points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"
        fill="url(#halfFill)"
      />
    </svg>
  );
}

// -----------------------------------------------------------------------------
// Static Rating Display (non-interactive)
// -----------------------------------------------------------------------------

interface RatingDisplayProps {
  rating: number;
  maxRating?: number;
  size?: 'sm' | 'md' | 'lg';
  showValue?: boolean;
  className?: string;
}

export function RatingDisplay({
  rating,
  maxRating = 5,
  size = 'md',
  showValue = true,
  className,
}: RatingDisplayProps) {
  const sizeConfig = {
    sm: { star: 'w-3.5 h-3.5', gap: 'gap-0.5', text: 'text-xs' },
    md: { star: 'w-4 h-4', gap: 'gap-0.5', text: 'text-sm' },
    lg: { star: 'w-5 h-5', gap: 'gap-1', text: 'text-base' },
  };

  const config = sizeConfig[size];

  return (
    <div className={cn('flex items-center', config.gap, className)}>
      <div className={cn('flex items-center', config.gap)}>
        {Array.from({ length: maxRating }, (_, i) => {
          const starValue = i + 1;
          const isFilled = starValue <= rating;
          const isHalfFilled = !isFilled && starValue - 0.5 <= rating;

          if (isHalfFilled) {
            return <HalfStar key={i} className={cn(config.star, 'text-terracotta')} />;
          }

          return (
            <Star
              key={i}
              className={cn(
                config.star,
                isFilled
                  ? 'fill-terracotta text-terracotta'
                  : 'text-warm-gray/30'
              )}
            />
          );
        })}
      </div>
      {showValue && (
        <span className={cn(config.text, 'text-navy font-medium ml-1')}>
          {rating.toFixed(1)}
        </span>
      )}
    </div>
  );
}

export default RatingWidget;
